import type { TExample } from '@atlas/uicorn/preview/modules';
import React from 'react';
import styled from 'styled-components';
import type { TThemeColor } from '@atlas/uicorn/lib/System/StylesProvider';

const Text = React.memo(function Text(props: TTextProps): React.ReactElement {
    return <SText {...convertProps(props)} />;
});

export default Text;

// #region Helpers

function convertProps(props: TTextProps) {
    const { ...scProps }: Partial<TTextProps> & TStyledProps = props;

    scProps.$color = scProps.color;
    delete scProps.color;

    scProps.as = 'span';
    if ('p' in scProps && scProps.p) {
        scProps.as = 'p';
        delete scProps.p;
    }
    if ('pre-line' in scProps && scProps['pre-line']) {
        scProps.as = 'p';
        scProps.$whiteSpace = 'pre-line';
        delete scProps['pre-line'];
    }
    if ('h' in scProps && scProps.h) {
        scProps.as = `h${scProps.h}`;
        delete scProps.h;
    }
    if ('caption' in scProps) {
        scProps.$caption = scProps.caption;
        delete scProps.caption;
    }
    if ('ellipsis' in scProps) {
        scProps.$ellipsis = scProps.ellipsis;
        delete scProps.ellipsis;
    }
    if ('noWrap' in scProps) {
        scProps.$noWrap = scProps.noWrap;
        delete scProps.noWrap;
    }
    if ('strong' in scProps) {
        scProps.$strong = scProps.strong;
        delete scProps.strong;
    }

    return scProps;
}

// #endregion

// #region Styles

const SText = styled.span<TStyledProps>`
    margin: 0;

    color: ${(p) => (p?.$color ? p.theme.color[p.$color] : 'inherit')};

    &:where(h1) {
        font: ${(p) => p.theme.font.h1};
    }
    &:where(h2) {
        font: ${(p) => p.theme.font.h2};
    }
    &:where(h3) {
        font: ${(p) => p.theme.font.h3};
    }
    &:where(h4) {
        font: ${(p) => p.theme.font.h4};
    }
    &:where(h5) {
        font: ${(p) => p.theme.font.h5};
    }
    &:where(p, span) {
        font: ${(p) => (p?.$caption ? p.theme.font.caption : p.theme.font.normal)};
        ${(p) => p.$strong && 'font-weight: 550;'}
        ${(p) => p.$whiteSpace && `white-space: ${p.$whiteSpace};`}
    }

    ${(p) => (p?.$ellipsis === true || p?.$ellipsis === 1) && p.theme.overflowText()}
    ${(p) => typeof p?.$ellipsis === 'number' && p?.$ellipsis > 1 && p.theme.patch.lineClampChangesDisplay(p.$ellipsis)}

    ${(p) => p?.$noWrap && 'white-space: nowrap;'}
`;

// #endregion

// #region Types

export type TTextProps = (
    | { caption?: true; ellipsis?: boolean | number; noWrap?: true; strong?: true }
    | { p: true; caption?: true; strong?: true }
    | { 'pre-line': true; caption?: true; strong?: true }
    | { h: '1' | '2' | '3' | '4' | '5' }
) & {
    className?: string;
    style?: React.CSSProperties;
    color?: TThemeColor;
    children: React.ReactNode;
};

type TSupportedTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'p' | 'span';

type TStyledProps = {
    as?: TSupportedTag;
    $caption?: true;
    $whiteSpace?: 'pre-line';
    $color?: TThemeColor;
    $ellipsis?: boolean | number;
    $noWrap?: true;
    $strong?: true;
};

// #endregion

// #region Examples

export const ConfigurableExample: TExample = {
    containerStyle: { border: '2px dashed green', maxWidth: '30vw' },
    render(props) {
        const { caption, children, color, ellipsis, h, noWrap, p, strong } = props;
        const elementProps = { caption, children, color, ellipsis, h, noWrap, p, strong };

        return <Text {...elementProps} />;
    },

    controls: {
        children: { type: 'string', initial: 'Welcome! I am Text Component, happy to serve your layouts' },
        h: { type: ['1', '2', '3', '4', '5'], connected: false },
        p: { type: 'boolean', initial: true, connected: false },
        caption: { type: 'boolean', initial: true, connected: false },
        strong: { type: 'boolean', initial: true, connected: false },
        ellipsis: { type: 'boolean', initial: true, connected: false },
        noWrap: { type: 'boolean', initial: true, connected: false },
        color: {
            type: [
                'primary',
                'grey9',
                'grey8',
                'grey7',
                'grey6',
                'grey5',
                'grey0',
                'red6',
                'red7',
                'green6',
                'yellow6',
            ],
            initial: 'grey6',
            connected: false,
        },
    },
};

export const ShowCaseExample: TExample = {
    containerStyle: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateRows: 'repeat(2, 1fr)',
        gridGap: '3.6rem',
    },
    render(props) {
        const sectionStyle: React.CSSProperties = {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            rowGap: '1.6rem',
        };
        const preStyle: React.CSSProperties = {
            height: '100%',
            margin: 0,
            fontSize: '1.1rem',
            whiteSpace: 'pre-wrap',
        };
        return (
            <>
                <section style={sectionStyle}>
                    <Text>
                        I am span — the default tag of <code>Text</code>. Color is inherited by default or can be passed
                        as a prop.
                    </Text>
                    <Text caption>I am a caption span</Text>
                    <Text caption color="grey8">
                        I am a colored caption span
                    </Text>

                    <div>
                        Dylan <Text color="grey8">assigned this ticket to</Text> self.{' '}
                        <Text color="grey8">Today, 2:14 PM</Text>
                    </div>

                    <div>
                        We may also <Text strong>emphasize something</Text> with <code>strong</code> prop.
                    </div>
                </section>

                <section style={sectionStyle}>
                    <Text p>
                        I am a paragraph. Say whatever you want, I'm going to display it smoothly.
                        <br />
                        This is our default body copy. It will wrap when needed.
                    </Text>
                    <Text p caption>
                        This is a caption. It usually won’t need to wrap. <br />
                        But it certainly can if really needed.
                    </Text>
                    <Text p color="grey8">
                        This is description copy. It has <code>color=`grey8`</code> applied.
                    </Text>
                    <Text p caption color="grey8">
                        This is colored caption (<code>`grey8`</code>).
                    </Text>
                </section>

                <section style={sectionStyle}>
                    <Text h="1">This is Heading 1. It will wrap when needed</Text>
                    <Text h="2">This is Heading 2. It will wrap when needed</Text>
                    <Text h="3">This is Heading 3. It will wrap when needed</Text>
                    <Text h="4">This is Heading 4. It will wrap when needed</Text>
                    <Text h="5">This is Heading 5. It will wrap when needed</Text>
                </section>

                {props?.hideCodeExamples ? null : (
                    <>
                        {/* Span code */}
                        {/* prettier-ignore */}
                        <pre style={preStyle}>
                            &lt;Text&gt;I am span — the default tag of <code>Text</code>. Color is inherited by default or can be passed
                        as a prop.&lt;/Text&gt;{'\n'}
                            &lt;Text caption&gt;I am a caption span&lt;/Text&gt;{'\n'}
                            &lt;Text caption color="grey8"&gt;I am a colored caption span&lt;/Text&gt;{'\n'}

                            &lt;div&gt;{'\n    '}
                                Dylan &lt;Text color="grey8"&gt;assigned this ticket to &lt;/Text&gt;self.{'\n    '}
                                &lt;Text color="grey8"&gt;Today, 2:14 PM&lt;/Text&gt;{'\n'}
                            &lt;/div&gt;{'\n'}

                            &lt;div&gt;{'\n    '}
                                We may also{' '}
                                &lt;Text strong&gt;emphasize something&lt;/Text&gt;{' '}
                            with &lt;code&gt;strong&lt;/code&gt; prop.{'\n'}&lt;/div&gt;{'\n'}
                        </pre>

                        {/* Paragraph code */}
                        {/* prettier-ignore */}
                        <pre style={preStyle}>
                            &lt;Text p&gt;{'\n    '}
                            I am a paragraph. Say whatever you want, I'm going to display it smoothly. &lt;br /&gt;{'\n    '}
                                This is our default body copy. It will wrap when needed.{'\n'}
                            &lt;/Text&gt;{'\n'}
                            &lt;Text p caption&gt;{'\n    '}
                                This is a caption. It usually won’t need to wrap. &lt;br /&gt;{'\n    '}
                                But it certainly can if really needed.{'\n    '}
                            &lt;/Text&gt;{'\n'}
                            &lt;Text p color="grey8"&gt;This is description copy. It has color assigned&lt;/Text&gt;{'\n'}
                            &lt;Text p caption color="grey8"&gt;This is colored caption&lt;/Text&gt;{'\n'}
                        </pre>

                        {/* Heading code */}
                        {/* prettier-ignore */}
                        <pre style={preStyle}>
                            &lt;Text h="1"&gt;This is Heading 1. It will wrap when needed&lt;/Text&gt;{'\n'}
                            &lt;Text h="2"&gt;This is Heading 2. It will wrap when needed&lt;/Text&gt;{'\n'}

                            &lt;Text h="3"&gt;This is Heading 3. It will wrap when needed&lt;/Text&gt;{'\n'}

                            &lt;Text h="4"&gt;This is Heading 4. It will wrap when needed&lt;/Text&gt;{'\n'}
                            &lt;Text h="5"&gt;This is Heading 5. It will wrap when needed&lt;/Text&gt;{'\n'}
                        </pre>
                    </>
                )}
            </>
        );
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Text', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<ShowCaseExample.render hideCodeExamples />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
